import { useMemo } from 'react'
import { type GetHookResponse, type SelectItem, useAccountStatuses, useTranslation } from '@front/model'
import { type ComboboxItemGroup } from '@mantine/core'
import {
  type AccountStatusData,
  type AccountStatusId,
  isDeleted,
  isNotEmpty,
  sortAccountStatuses,
  splitArray,
} from '@terros/common'

type Props = {
  availableStatuses?: AccountStatusId[]
}

export const useAvailableStatuses = ({ availableStatuses }: Props): GetHookResponse<AccountStatusData[]> => {
  const state = useAccountStatuses({ archived: 'all' })
  const statuses = useMemo(() => {
    if (state.data?.includesDefaults) return []
    const statuses = state.data?.statuses
    if (isNotEmpty(availableStatuses)) return statuses?.filter((s) => availableStatuses.includes(s.statusId))
    return statuses
  }, [state.data?.statuses, availableStatuses, state.data?.includesDefaults])
  return { ...state, data: statuses }
}

export const useAccountStageSelectItems = ({
  availableStatuses,
  allowArchived = false,
  isDisabled = () => false,
}: Props & { allowArchived?: boolean; isDisabled?: (statusId: AccountStatusId) => boolean }): GetHookResponse<
  ComboboxItemGroup[]
> => {
  const { t } = useTranslation()
  const { data, ...state } = useAvailableStatuses({ availableStatuses })
  const items: ComboboxItemGroup[] = useMemo(() => {
    const { yes: archived, no: active } = splitArray(data ?? [], isDeleted)
    const groups: ComboboxItemGroup[] = [
      { group: t('Account Stages'), items: sortAccountStatuses(active).map((s) => toStageItem(s, false)) },
    ]

    if (isNotEmpty(archived)) {
      groups.push({
        group: t('Archived Account Stages'),
        items: sortAccountStatuses(archived).map((stage) =>
          toStageItem(stage, isDisabled(stage.statusId) || !allowArchived)
        ),
      })
    }
    return groups
  }, [data, allowArchived, isDisabled, t])
  return { ...state, data: items }
}

const toStageItem = (status: AccountStatusData, disabled: boolean): SelectItem<AccountStatusId> => ({
  value: status.statusId,
  label: status.name,
  disabled,
})
